import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { btnPrimaryCls } from "components/tw/classes";
import StatusChip, { ChipTone } from "components/tw/status-chip";
import { AssetActStatus, MyAsset } from "services/cabinet-me.service";

/**
 * What a card says about its act. It is looked up by the act's `status` the server gives.
 * `chipKey` and `tone` make the label; `opensDocument` adds the button that goes to the
 * document page, where the act is read and signed (nothing is signed from the card).
 */
const ACT_STATUSES: Record<AssetActStatus, { chipKey: string; tone: ChipTone; opensDocument?: boolean }> = {
  signed: { chipKey: "cabinet_asset_act_signed", tone: "green" },
  to_sign: { chipKey: "cabinet_asset_act_to_sign", tone: "amber", opensDocument: true },
  waiting_others: { chipKey: "cabinet_asset_act_waiting_others", tone: "grey" },
  none: { chipKey: "cabinet_asset_act_none", tone: "grey" },
};

/** One item the person holds: what it is, since when, and the state of its act. */
export default function AssetCard({ asset }: { asset: MyAsset }) {
  const { t } = useTranslation();
  const { chipKey, tone, opensDocument } = ACT_STATUSES[asset.act.status];
  const typeAndModel = [asset.type, [asset.brand, asset.model].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(" · ");

  return (
    <li className="flex flex-col gap-1 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="text-base font-semibold text-gray-900">{asset.name}</h3>
      {typeAndModel && <p className="text-sm text-gray-500">{typeAndModel}</p>}
      {asset.serial && <p className="text-sm text-gray-500">{t("cabinet_asset_serial", { serial: asset.serial })}</p>}
      {asset.heldSince && (
        <p className="text-sm text-gray-500">{t("cabinet_asset_held_since", { date: asset.heldSince })}</p>
      )}
      <div className="mt-2">
        <StatusChip tone={tone}>{t(chipKey)}</StatusChip>
      </div>
      {opensDocument && asset.act.link && (
        <Link to={asset.act.link} className={`${btnPrimaryCls} mt-2 w-full`}>
          {t("cabinet_asset_act_open")}
        </Link>
      )}
    </li>
  );
}
